import { useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

const Privacy = () => {
  const [language, setLanguage] = useState<"en" | "fr">("en");

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          value={language}
          exclusive
          onChange={(_, nextLanguage) => {
            if (nextLanguage) {
              setLanguage(nextLanguage);
            }
          }}
        >
          <ToggleButton value="en">EN</ToggleButton>
          <ToggleButton value="fr">FR</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {language === "en" ? (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy (Web App)
          </Typography>

          <Typography paragraph>Effective Date: 2024-01-01</Typography>
          <Typography paragraph>Last Updated: 2026-03-01</Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            L’API-L (“we”, “our”, “us”) operates this web application (the “Web
            App”).
          </Typography>
          <Typography paragraph>
            This Privacy Policy applies to the website/web application. It does
            not apply to the iOS mobile app, which has a separate privacy policy
            at https://dicoapi.ch/privacy-ios.
          </Typography>
          <Typography paragraph>
            This Privacy Policy explains how information is processed when you
            use the Web App.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            2. Data We Process
          </Typography>
          <Typography paragraph>
            The Web App uses Firebase Analytics to understand usage and improve
            functionality.
          </Typography>
          <Typography paragraph>
            Analytics events in this project include feature interactions (for
            example, search usage, result selection, and game checks).
          </Typography>
          <Typography paragraph>
            We do not use advertising SDKs and we do not use analytics for
            personalized advertising.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            3. Purposes of Processing
          </Typography>
          <Typography paragraph>We process data to:</Typography>
          <Typography paragraph>
            - Maintain and improve reliability and performance of the Web App
          </Typography>
          <Typography paragraph>
            - Understand feature usage at an aggregate level
          </Typography>
          <Typography paragraph>We do not sell personal data.</Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            4. Legal Basis (Where Applicable)
          </Typography>
          <Typography paragraph>
            Depending on your jurisdiction, processing is based on legitimate
            interests (security, service operation, and improvement) and/or your
            consent where required for analytics technologies.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            5. Third-Party Processors
          </Typography>
          <Typography paragraph>
            We use third-party providers to operate the Web App:
          </Typography>
          <Typography paragraph>
            - Firebase Analytics (Google) for usage analytics
          </Typography>
          <Typography paragraph>
            These providers may process technical identifiers and related
            metadata in accordance with their own terms and privacy
            documentation.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            6. Data Retention
          </Typography>
          <Typography paragraph>
            Analytics data are retained according to Firebase configuration and
            provider retention settings, and as needed for operational purposes.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            7. Security
          </Typography>
          <Typography paragraph>
            We implement reasonable technical and organizational measures to
            protect data and secure communications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            8. International Transfers
          </Typography>
          <Typography paragraph>
            Data processed through third-party services may be transferred and
            stored in countries outside your own jurisdiction.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            9. Your Rights
          </Typography>
          <Typography paragraph>
            Depending on applicable law, you may have rights to request access,
            correction, deletion, restriction, or objection regarding personal
            data, and to withdraw consent where consent is used.
          </Typography>
          <Typography paragraph>
            You may also have the right to lodge a complaint with a competent
            data protection authority.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            10. Changes to This Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy if the Web App or its data
            practices change.
          </Typography>
          <Typography paragraph>
            Any changes will be posted at: https://dicoapi.ch/privacy
          </Typography>
          <Typography paragraph>
            The “Last Updated” date will reflect modifications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            11. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy, you may contact
            us at:
          </Typography>
          <Typography paragraph>L’API-L</Typography>
          <Typography paragraph>Email: contact@lapil.ch</Typography>
        </>
      ) : (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            Politique de confidentialité (Application Web)
          </Typography>

          <Typography paragraph>
            Date d’entrée en vigueur : 2024-01-01
          </Typography>
          <Typography paragraph>Dernière mise à jour : 2026-03-01</Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            L’API-L (« nous », « notre », « nos ») exploite cette application
            web (l’« Application Web »).
          </Typography>
          <Typography paragraph>
            Cette politique de confidentialité s’applique au site/application
            web. Elle ne s’applique pas à l’application mobile iOS, qui dispose
            d’une politique distincte à l’adresse
            https://dicoapi.ch/privacy-ios.
          </Typography>
          <Typography paragraph>
            Cette politique explique comment les informations sont traitées
            lorsque vous utilisez l’Application Web.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            2. Données que nous traitons
          </Typography>
          <Typography paragraph>
            L’Application Web utilise Firebase Analytics pour comprendre
            l’utilisation du service et améliorer ses fonctionnalités.
          </Typography>
          <Typography paragraph>
            Les événements analytiques incluent notamment des interactions avec
            les fonctionnalités (par exemple, l’usage de la recherche, la
            sélection d’un résultat et les vérifications de jeu).
          </Typography>
          <Typography paragraph>
            Nous n’utilisons pas de SDK publicitaires et nous n’utilisons pas
            l’analytics pour la publicité personnalisée.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            3. Finalités du traitement
          </Typography>
          <Typography paragraph>Nous traitons les données afin de :</Typography>
          <Typography paragraph>
            - Maintenir et améliorer la fiabilité et les performances de
            l’Application Web
          </Typography>
          <Typography paragraph>
            - Comprendre l’usage des fonctionnalités de manière agrégée
          </Typography>
          <Typography paragraph>
            Nous ne vendons pas de données personnelles.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            4. Base légale (le cas échéant)
          </Typography>
          <Typography paragraph>
            Selon votre juridiction, le traitement repose sur nos intérêts
            légitimes (sécurité, fonctionnement du service et amélioration)
            et/ou sur votre consentement lorsque celui-ci est requis pour les
            technologies d’analytics.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            5. Sous-traitants tiers
          </Typography>
          <Typography paragraph>
            Nous utilisons des prestataires tiers pour exploiter l’Application
            Web :
          </Typography>
          <Typography paragraph>
            - Firebase Analytics (Google) pour les statistiques d’usage
          </Typography>
          <Typography paragraph>
            Ces prestataires peuvent traiter des identifiants techniques et des
            métadonnées associées conformément à leurs propres conditions et
            politiques de confidentialité.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            6. Conservation des données
          </Typography>
          <Typography paragraph>
            Les données analytiques sont conservées selon la configuration
            Firebase et les paramètres de conservation du fournisseur, ainsi que
            selon les besoins opérationnels.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            7. Sécurité
          </Typography>
          <Typography paragraph>
            Nous mettons en œuvre des mesures techniques et organisationnelles
            raisonnables pour protéger les données et sécuriser les
            communications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            8. Transferts internationaux
          </Typography>
          <Typography paragraph>
            Les données traitées via des services tiers peuvent être transférées
            et stockées dans des pays situés en dehors de votre juridiction.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            9. Vos droits
          </Typography>
          <Typography paragraph>
            Selon la loi applicable, vous pouvez disposer de droits d’accès, de
            rectification, de suppression, de limitation ou d’opposition
            concernant vos données personnelles, ainsi que du droit de retirer
            votre consentement lorsque celui-ci constitue la base du traitement.
          </Typography>
          <Typography paragraph>
            Vous pouvez également avoir le droit d’introduire une réclamation
            auprès d’une autorité compétente en matière de protection des
            données.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            10. Modifications de cette politique
          </Typography>
          <Typography paragraph>
            Nous pouvons mettre à jour cette politique de confidentialité si
            l’Application Web ou ses pratiques de traitement évoluent.
          </Typography>
          <Typography paragraph>
            Toute modification sera publiée à l’adresse :
            https://dicoapi.ch/privacy
          </Typography>
          <Typography paragraph>
            La date de « Dernière mise à jour » reflète les modifications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            11. Contact
          </Typography>
          <Typography paragraph>
            Si vous avez des questions concernant cette politique de
            confidentialité, vous pouvez nous contacter à :
          </Typography>
          <Typography paragraph>L’API-L</Typography>
          <Typography paragraph>Email : contact@lapil.ch</Typography>
        </>
      )}
    </Box>
  );
};

export default Privacy;
